import { Avtaar, Box, BoxProps, ColorOptions, Icon, Image, ImageProps, SearchBar, Spacer, Text } from '@jfs/ui-new'
import { isIE } from '@jfs/ui-new/src/utils/utils'
import * as React from 'react'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { Platform } from 'react-native'
import { Modal, ModalTypes } from '../../comps/Modal'
import { useStores } from '../../data/store'
import { convertHexToRGBA } from '../../utils'
import { PAYMENT_MODE_SUBTYPES } from '../../utils/constants'
import { CustomRecyclerView } from '../CustomRecyclerView'
import { PaySecurelyButton } from '../PaySecurelyButton'

export interface DropDownData {
  text?: string
  imageUrl?: string
  id?: string
}

export interface DropdownProps {
  ID?: string
  data: Array<DropDownData>
  type?: number
  label?: string
  placeHolder?: string
  onChange?: (value: string) => void
  value?: string
  isModalSizeFixed?: boolean
  modalPosition?: keyof typeof ModalTypes
  displayPayButton?: boolean
  setSelectedItem: any
  onClickPaySecure: any
  showSubTitle?: any
  isLoading?: boolean
  convFeeComp?: React.ReactNode
  amount?: string
}

export interface RefCalback {
  isEditable(): boolean
  focus(): void
}

const MIN_DATA_TO_SHOW = 10

const getImage = (item: any, type: any) => {
  if (item?.imageUrl) {
    return <Avtaar hide={!item?.imageUrl} source={item?.imageUrl ?? ''} type="medium" title={item?.text ?? ''} />
  } else {
    return (
      <Image
        {...styles.imageView}
        source={
          type === PAYMENT_MODE_SUBTYPES.WALLET
            ? require('../../assets/images/wallet.png')
            : require('../../assets/images/bank.png')
        }
      />
    )
  }
}

const ListItem = ({
  item,
  onPress,
  type,
  index,
  selectedItem,
  theme,
  showSubTitle,
  convFeeComp,
}: {
  item: DropDownData
  onPress: (item: DropDownData) => void
  type: number
  index: number
  selectedItem: any
  theme: any
  showSubTitle: any
  convFeeComp: any
}) => {
  const handlePress = () => onPress(item)
  const title = item?.text ?? ''

  return (
    <Box height={64}>
      <Box
        {...styles.container}
        onPress={handlePress}
        underlayColor={convertHexToRGBA(theme?.brandColor, 10) as ColorOptions}
        borderBottomColor={'SEPERATOR'}
        f={1}
        testID={`bankId-${item?.id}`}>
        <>
          <Box f={1} alignItems="center" flexDirection={'row'}>
            {getImage(item, type)}
            <Box padding={16} flexShrink={1}>
              <Text
                fontFamily="jioType-medium"
                fontSize={12}
                color={selectedItem ? (theme?.brandColor as ColorOptions) : (theme?.bodyTextColor as ColorOptions)}>
                {title}
              </Text>
              {selectedItem && showSubTitle !== '' && (
                <Text
                  fontFamily="jiotype-medium-italic"
                  fontSize={12}
                  color={convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions}
                  value={showSubTitle}
                />
              )}
              {convFeeComp && selectedItem && convFeeComp}
            </Box>
          </Box>
          {selectedItem && <Icon id="ROUND_TICK" color={theme?.brandColor as ColorOptions} size={24} />}
        </>
      </Box>
      <Box {...styles.separatorStyle(theme)} />
    </Box>
  )
}

export const DropdownList = forwardRef((props: DropdownProps, ref: React.Ref<RefCalback>) => {
  const {
    data,
    type = PAYMENT_MODE_SUBTYPES.NB,
    label,
    placeHolder = `Select ${props.label}`,
    onChange,
    value,
    isModalSizeFixed = false,
    modalPosition = 'Bottom',
    displayPayButton,
    setSelectedItem: parentSetSelectedItem,
    onClickPaySecure: parentOnClickPaySecure,
    showSubTitle,
    isLoading = false,
    convFeeComp,
    amount,
  } = props
  let modifiedValue = value
  if (!modifiedValue) {
    modifiedValue = placeHolder
  }

  const { setSelectedPayment, getTransactionAmount, closeModal, updateModalDisplaying, updateCancelModal, theme } =
    useStores((root) => ({
      setSelectedPayment: root.setSelectedPayment,
      getTransactionAmount: root.getTransactionAmount(),
      closeModal: root.config.closeModal,
      updateModalDisplaying: root.config.updateModalDisplaying,
      updateCancelModal: root.config.updateCancelModal,
      theme: root?.config?.merchantTheme,
    }))

  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState('')

  const handleOpen = useCallback(
    (isOpen: boolean) => {
      // If the list length is more than zero then only will show modail
      if (data.length > 0) {
        updateModalDisplaying(isOpen)
        setOpen(isOpen)
      }
    },
    [data.length, updateModalDisplaying],
  )

  useEffect(() => {
    if (closeModal) {
      updateModalDisplaying(false)
      setOpen(false)
      updateCancelModal(false)
    }
  }, [closeModal, updateCancelModal, updateModalDisplaying])

  useImperativeHandle(ref, () => ({
    isEditable: () => {
      return true
    },
    focus: () => handleOpen(true),
  }))

  const [selectedItem, setSelectedItem] = useState('')

  useEffect(() => {
    const currentSelectedItem: any = data.find((item) => item.text === modifiedValue)
    if (currentSelectedItem && currentSelectedItem?.id !== selectedItem) {
      setSelectedItem(currentSelectedItem.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifiedValue])

  const onHandlePress = (item: DropDownData) => {
    if (onChange) {
      parentSetSelectedItem(item?.id ?? '')
      setSelectedItem(item?.id ?? '')
      if (!displayPayButton) {
        console.log('closing modal')
        handleOpen(false)
        parentSetSelectedItem(item?.id ?? '')
        onChange(item?.text ?? '')
      }
    }
  }

  const getListData = () => {
    const searchfn = (item: DropDownData) => {
      const search = searchText.toLowerCase()
      const text = item?.text?.toLowerCase() ?? ''
      return text.includes(search)
    }
    const listData = data.filter(searchfn)

    return listData
  }

  const onChangeText = (text: string) => {
    setSearchText(text)
  }

  const renderItem = ({ item, index }: { item: any; index: any }) => {
    return (
      <ListItem
        item={item}
        onPress={onHandlePress}
        index={index}
        type={type}
        selectedItem={selectedItem === item?.id}
        theme={theme}
        showSubTitle={showSubTitle}
        convFeeComp={convFeeComp}
      />
    )
  }

  const renderEmptyView = () => {
    return (
      <Box {...styles.emptyContainer}>
        <Text color={theme.bodyTextColor as ColorOptions} fontFamily="jioType-medium">
          Search not found
        </Text>
      </Box>
    )
  }

  const onClickPaySecure = () => {
    setSelectedPayment(selectedItem, type)

    parentOnClickPaySecure()
    handleOpen(false)
  }

  const onDismiss = () => {
    if (displayPayButton) {
      parentSetSelectedItem(selectedItem ?? '')
    }
    handleOpen(false)
    setSearchText('')
  }

  return (
    <Box
      onPress={() => {
        handleOpen(true)
      }}
      underlayColor={'TRANSPARENT'}
      width={'100%'}
      pointerEvents={Platform.OS === 'web' ? 'box-only' : 'auto'}
      testID={'open-bank-dropdown'}>
      <Box flexDirection="row" paddingVertical={8}>
        <Box f={1} paddingHorizontal={8}>
          <Text
            value={modifiedValue}
            fontSize={14}
            fontFamily="jioType-Medium"
            color={theme?.brandColor as ColorOptions}
          />
        </Box>
        <Box paddingRight={16}>
          <Icon color={theme?.brandColor as ColorOptions} id="SEARCH" size={20} />
        </Box>
      </Box>

      <Modal
        type={modalPosition}
        title={label ?? ''}
        headerType="White_Bg"
        show={open}
        onDismiss={onDismiss}
        theme={theme}
        containerTopPadding={isIE() ? 5 : undefined}>
        <Box
          paddingVertical={8}
          paddingLeft={8}
          bg={theme?.bodyBgColor as ColorOptions} //"WHITE"
          {...(isModalSizeFixed ? styles.boxContainer : { maxHeight: '80%', bottom: 2 })}>
          <Spacer size={8} />
          <Box paddingHorizontal={8}>
            <SearchBar
              hide={data.length <= MIN_DATA_TO_SHOW}
              onChange={onChangeText}
              placeholder="Search using name"
              value={searchText}
              autoFocus={open}
              theme={theme}
            />
          </Box>
          <Box paddingVertical={8} flex={1}>
            <CustomRecyclerView
              nativeID={'showListScrollBar'}
              data={getListData()}
              renderItem={renderItem}
              ListEmptyComponent={renderEmptyView}
            />
          </Box>
          {displayPayButton && (
            <Box paddingTop={8} paddingRight={16} paddingLeft={8}>
              <PaySecurelyButton
                isLoading={isLoading}
                testID={'paySecurelyBtn'}
                amount={amount ?? getTransactionAmount}
                onPress={onClickPaySecure}
                disabled={!selectedItem}
              />
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  )
})

const styles = {
  container: {
    flexDirection: 'row',
    // paddingVertical: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
  } as BoxProps,
  boxContainer: {
    height: '90%',
    // width: ['80vw', '80vw'],
  } as BoxProps,
  emptyContainer: {
    height: '45vh',
    wight: '50vw',
    justifyContent: 'center',
    alignItems: 'center',
  } as BoxProps,
  imageView: {
    height: 32,
    width: 32,
  } as ImageProps,
  separatorStyle: (theme: any) =>
    ({ height: 1, backgroundColor: convertHexToRGBA(theme?.brandColor, 10) as ColorOptions } as BoxProps),
}
